import { useState } from 'react';
import Sheet from '../../shared/ui/Sheet';
import MapView from '../../shared/MapView';
import { StatusBadge } from '../../shared/ui';
import { driverPayoutFor, distanceMiles, formatCurrency, orderPickupParish, normalizeParish } from '../../shared/models';
import { driverAcceptOrder } from '../../shared/services/orderService';
import { restaurantCoords, customerCoords, deliveryAddressText, restaurantAddressText } from './geo';
import NavButtons from './NavButtons';

// Delivery preview (mirrors AvailableOrderCard.DeliveryPreviewView):
// mini route map, distance/earnings, pickup + delivery cards with external
// navigation links, and the Accept button (hidden at capacity).
export default function OrderPreviewSheet({ order, onClose, atCapacity, driverInfo, onAccepted }) {
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  const pickup = restaurantCoords(order);
  const dropoff = customerCoords(order);
  const miles = pickup && dropoff ? distanceMiles(pickup, dropoff) : null;
  const payout = order ? driverPayoutFor(order) : 0;
  const num = order ? String(order.orderNumber || order.id).slice(0, 8) : '';
  const itemCount = order ? (order.orderItems || order.items || []).length : 0;

  const markers = [
    pickup && { ...pickup, kind: 'restaurant', popup: order.restaurantName || 'Pickup' },
    dropoff && { ...dropoff, kind: 'customer', popup: 'Customer' },
  ].filter(Boolean);
  const route = pickup && dropoff ? [pickup, dropoff] : null;

  async function accept() {
    setAccepting(true);
    setError(null);
    try {
      await driverAcceptOrder(order, driverInfo);
      onAccepted(order);
    } catch (e) {
      setError(e.message || 'This order could not be accepted.');
      setAccepting(false);
    }
  }

  return (
    <Sheet open={!!order} onClose={onClose} title="Delivery Details" maxHeight="92vh">
      {order && (
        <div className="space-y-4 p-4 pt-1">
          <div className="flex items-center justify-between">
            <p className="text-base font-extrabold text-gray-900">Order #{num}</p>
            <StatusBadge status={order.status || 'Ready'} />
          </div>

          {/* Route overview */}
          <div>
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-gray-400">
              Route Overview
            </p>
            <div className="h-56 overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
              <MapView markers={markers} route={route} />
            </div>
            <div className="mt-2 flex items-center justify-between rounded-xl bg-white p-3 shadow-sm border border-gray-100">
              <div>
                <p className="text-[10px] font-medium uppercase text-gray-400">Distance</p>
                <p className="text-sm font-bold text-gray-900">
                  {miles != null ? `${miles.toFixed(1)} miles` : '—'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-medium uppercase text-gray-400">Your Earnings</p>
                <p className="text-sm font-extrabold text-green-600">
                  {formatCurrency(payout)}
                </p>
              </div>
            </div>
          </div>

          {/* Pickup */}
          <div>
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-gray-400">
              Pickup Location
            </p>
            <div className="space-y-1.5 rounded-2xl bg-white p-3 shadow-sm border border-gray-100">
              <p className="text-sm font-bold text-gray-900">
                🏪 {order.restaurantName || 'Restaurant'}
              </p>
              {restaurantAddressText(order) && (
                <p className="text-xs text-gray-500">{restaurantAddressText(order)}</p>
              )}
              {orderPickupParish(order) && (
                <p className="text-xs text-gray-500">Zone: {orderPickupParish(order)}</p>
              )}
              <NavButtons coords={pickup} />
            </div>
          </div>

          {/* Delivery */}
          <div>
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-gray-400">
              Delivery Information
            </p>
            <div className="space-y-1.5 rounded-2xl bg-white p-3 shadow-sm border border-gray-100">
              <p className="text-sm font-bold text-gray-900">🏠 {order.customerName || 'Customer'}</p>
              {deliveryAddressText(order) && (
                <p className="text-xs text-gray-500">{deliveryAddressText(order)}</p>
              )}
              {normalizeParish(order.deliveryAddress?.parish || order.raw?.deliveryAddress?.parish) && (
                <p className="text-xs text-gray-500">
                  Drop-off parish: {normalizeParish(order.deliveryAddress?.parish || order.raw?.deliveryAddress?.parish)}
                </p>
              )}
              {order.specialInstructions ? (
                <p className="text-xs text-[#FF6B35]">📝 {order.specialInstructions}</p>
              ) : null}
              <NavButtons coords={dropoff} />
            </div>
          </div>

          {/* Earnings */}
          <div>
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-gray-400">
              Earnings
            </p>
            <div className="space-y-1 rounded-2xl bg-white p-3 shadow-sm border border-gray-100">
              {order.tipAmount > 0 && (
                <p className="text-xs text-gray-500">
                  Tip: {formatCurrency(order.tipAmount)}
                </p>
              )}
              <p className="text-base font-extrabold text-green-600">
                Your Payout: {formatCurrency(payout)}
              </p>
              <p className="text-[11px] text-gray-400">
                {itemCount} item{itemCount === 1 ? '' : 's'} • Order total{' '}
                {formatCurrency(order.totalAmount)}
              </p>
            </div>
          </div>

          {error && (
            <p className="rounded-xl bg-red-50 p-3 text-center text-xs font-semibold text-red-600">
              {error}
            </p>
          )}

          {atCapacity ? (
            <div className="rounded-2xl bg-gray-100 p-3 text-center text-xs font-semibold text-gray-500">
              You're at capacity (2 active deliveries). Complete one before accepting more.
            </div>
          ) : (
            <button
              onClick={accept}
              disabled={accepting}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-green-500 py-3.5 text-base font-bold text-white shadow-sm disabled:opacity-60 active:scale-[0.98]"
            >
              {accepting ? '⏳ Accepting…' : '✅ Accept Delivery'}
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
}
